import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { IModalStyles, Modal, setFocusVisibility, Stack } from '@fluentui/react';
import clsx from 'clsx';
import { PillButton } from 'common/components/Buttons/v8';
import { BackIcon } from 'common/components/Icons';
import { Snackbar, SnackbarLayerHost } from 'common/components/Notifications';
import { ModalShimmer } from 'common/components/Shimmer/DefaultShimmers/ModalShimmer';
import { performanceService } from 'common/utilities/PerformanceService';
import { useLocalizedStrings } from 'i18n';
import { useAppSelector } from 'store';
import { useRaiseWebViewRenderedEvent, useWebView, useWebViewDialogContext } from 'store/webView/useWebView';

import { useFullScreenCard } from './DialogCard.helper';
import { IDialogCardProps } from './DialogCard.Props';
import { getMergedCustomStylesWithRoot, useDialogStyles, useWebViewDialogStyles } from './DialogCard.styles';
import { DialogCardCloseButton } from './DialogCardCloseButton';
import { DialogCardError } from './DialogCardError';
import { DialogCardFooter } from './DialogCardFooter';

export const dialog_card_layer_host_id = 'dialog-card-layer-host';

const DialogCard: React.FunctionComponent<IDialogCardProps> = (props: IDialogCardProps) => {
  const {
    title,
    onCloseClick,
    closeAriaLabel,
    enableBackNavigation,
    onBackLinkClick,
    children,
    isOpen,
    isBlocking,
    onRenderFooter,
    isLoading,
    disableFullScreenCard = false,
    onRenderLoading,
    webViewClassName,
    webViewDialogStyles,
    error,
    notification,
    clearNotification,
    styles: customStyles = {} as Partial<IModalStyles>,
    ...modalProps
  } = props;
  const styles = useDialogStyles();
  const webViewStyles = useWebViewDialogStyles(webViewDialogStyles);
  const mergedCustomStylesWithRoot = getMergedCustomStylesWithRoot(customStyles);
  const { isWebView } = useWebView();
  const { showWebViewDialogCloseButton } = useWebViewDialogContext();

  const { useCustomScrollBar } = useAppSelector((state) => state.layout);
  const isFullScreenCard = useFullScreenCard();
  const { t } = useLocalizedStrings(); // using default namespace

  useEffect(() => {
    if (isWebView && !error && !isLoading) {
      // Ensure that all focus outlines are visible in webview:
      setFocusVisibility(true);
    }
  }, [error, isLoading, isWebView]);

  useEffect(() => {
    performanceService.addSplitTiming(`DialogCard.Mount`);
  }, []);

  useRaiseWebViewRenderedEvent('DialogCard', isLoading, !!error);

  const loadingComponent = () => {
    return onRenderLoading ? onRenderLoading() : <ModalShimmer height="100%" />;
  }; // TODO: replace with actual loading component

  const bodyElem = (
    <Stack.Item className={styles.body} grow>
      {children}
    </Stack.Item>
  );

  const renderFooter = React.useCallback(
    () => (onRenderFooter ? <DialogCardFooter>{onRenderFooter()}</DialogCardFooter> : null),
    [onRenderFooter]
  );

  // Must use SnackbarLayerHost to render notification from within
  // the modal, otherwise events will not propagate and screen
  // readers can't access the text
  const renderNotification = (): JSX.Element | null => {
    if (notification) {
      return (
        <Snackbar
          message={notification.message}
          messageType={notification.messageType}
          layerHostId={dialog_card_layer_host_id}
          onDismiss={clearNotification}
        />
      );
    }
    return null;
  };

  const renderError = React.useCallback(() => {
    if (error) {
      return <DialogCardError title={error.title} subtitle={error.subtitle} />;
    }
    return null;
  }, [error]);

  const renderHeaderBackNavigation = (): JSX.Element => {
    const backIcon = <BackIcon />;

    return typeof onBackLinkClick === 'function' ? (
      <PillButton onClick={onBackLinkClick} text={t('Back')} onRenderIcon={() => backIcon} />
    ) : (
      <PillButton {...onBackLinkClick} text={t('Back')} onRenderIcon={() => backIcon} />
    );
  };

  if (isWebView) {
    if (error) {
      return renderError();
    }
    if (isLoading) {
      return loadingComponent();
    }
    return createPortal(
      <div
        className={clsx(webViewStyles.viewport, useCustomScrollBar && webViewStyles.customScrollBarStyle, webViewClassName)}
        aria-labelledby={modalProps.titleAriaId}
      >
        <Stack verticalAlign="start" className={webViewStyles.container}>
          {showWebViewDialogCloseButton && <DialogCardCloseButton onCloseClick={onCloseClick} closeAriaLabel={closeAriaLabel} />}
          <Stack className={webViewStyles.contents} grow>
            {enableBackNavigation && <div className={webViewStyles.header}>{renderHeaderBackNavigation()}</div>}
            {bodyElem}
          </Stack>
          {renderFooter()}
        </Stack>
        {renderNotification()}
        <SnackbarLayerHost id={dialog_card_layer_host_id} />
      </div>,
      document.body
    );
  }

  // TODO: Update render on empty page.
  if (isOpen && !disableFullScreenCard && isFullScreenCard) {
    if (error) {
      return renderError();
    }
    if (isLoading) {
      return loadingComponent();
    }
    return (
      <>
        {renderHeaderBackNavigation()}
        {children && <div>{children}</div>}
        {renderFooter()}
        {renderNotification()}
      </>
    );
  }

  return (
    <Modal
      allowTouchBodyScroll
      isAlert={false}
      isOpen={isOpen}
      isBlocking={isBlocking !== false}
      {...modalProps}
      styles={mergedCustomStylesWithRoot}
      aria-busy={isLoading}
    >
      <Stack verticalAlign="start" className={styles.container} verticalFill>
        {onCloseClick && <DialogCardCloseButton onCloseClick={onCloseClick} closeAriaLabel={closeAriaLabel} />}
        <Stack.Item className={styles.scrollingWrapper} grow>
          <Stack verticalFill>
            {enableBackNavigation && (
              <Stack.Item grow={false} className={styles.backButtonWrapper}>
                {renderHeaderBackNavigation()}
              </Stack.Item>
            )}
            {title && <Stack.Item grow={false}>{title}</Stack.Item>}
            {isLoading && loadingComponent()}
            {!isLoading && error && renderError()}
            {!isLoading && !error && bodyElem}
            {!isLoading && !error && renderFooter()}
          </Stack>
        </Stack.Item>
      </Stack>
      {renderNotification()}
      <SnackbarLayerHost
        id={dialog_card_layer_host_id}
        // Do not cover the footer buttons:
        bottomPosition={60}
      />
    </Modal>
  );
};

export default DialogCard;
